import React from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  Field,
  FieldProps,
  useField,
} from 'formik';
import TextField from 'hellospa/foundation/control/text-field';
import ErrorMessage from 'hellospa/components/error-message';
import * as Dropdown from 'hellospa/foundation/control/dropdown';
import ContactInput from 'hellospa/components/contact-input';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import HelpIcon from './icons/help';
import RemoveIcon from './icons/close';
import TooltipUI, {
  ArrowDirection,
} from 'hellospa/foundation/control/tooltip/tooltip-ui';
import * as types from 'hellospa/page/prep-and-send/data/types';
import styles from './index.scss';
import messages from './intl';
import classNames from 'classnames';

type AuthenticationTypes = 'accessCode' | 'smsAuthNumber'

type Props = {
  index: number,
  recipient: types.Signer | types.Recipient,
  onClose: () => void,
}

interface InitialValues {
  authType: AuthenticationTypes;
}

const AuthenticationFields = (props: Props) => {
  const intl = useIntl();
  const parent = `recipients.${props.index}`;
  const [recipientsField] = useField<types.Signer[]>('recipients');
  const accessCodeFieldName = `${parent}.accessCode`;
  const smsAuthFieldName = `${parent}.smsAuthNumber`;
  const deliveryTypeFieldName = `${parent}.deliveryType`;
  const [deliveryTypeField] = useField<String>(deliveryTypeFieldName);
  const [, accessCodeMeta, accessCodeHelpers] = useField<String>(accessCodeFieldName);
  const [, smsAuthNumberMeta, smsAuthNumberHelpers] = useField<String>(smsAuthFieldName);
  const [, authTypeMeta, authTypeHelpers] = useField<InitialValues['authType']>(`${parent}.authType`);

  const allowRecipientSmsAuth = useSelector(selectors.allowRecipientSmsAuth);
  const allowRecipientAccessCode = useSelector(selectors.allowRecipientAccessCode);
  const allowSmsSignatureDelivery = useSelector(selectors.allowSmsSignatureDelivery);
  const canUpgradeSmsSignatureDeliveryTier = useSelector(selectors.canUpgradeSmsSignatureTier);

  const isDefaultOrAccessCode = allowRecipientAccessCode && (!authTypeMeta.value || authTypeMeta.value === 'accessCode');

  const isSmsAuth = allowRecipientSmsAuth && (authTypeMeta.value === 'smsAuthNumber' || !isDefaultOrAccessCode);
  const isSmsDelivery = deliveryTypeField.value === types.RecipientDeliveryTypes.EmailAndSms;

  const dropdownButtonText = isDefaultOrAccessCode
    ? intl.formatMessage(messages.accessCodeLabel)
    : intl.formatMessage(messages.recipientAuthenticationDropdownSmsAuth);

  const [displayTooltip, setDisplayTooltip] = React.useState(false);
  const [tooltipPosition, setTooltipPosition] = React.useState({ top: '0', left: '0' });

  const MAX_SMS_AUTH = 5;

  const numOfSmsAuths = React.useMemo(() => {
    return recipientsField.value.reduce((acc: number, r: types.Signer) => {
      if (r.authType === 'smsAuthNumber') {
        return acc + 1;
      }
      return acc;
    }, 0);
  }, [recipientsField.value]);

  const smsAuthMenuItemDisabled = isSmsDelivery
  || (numOfSmsAuths === MAX_SMS_AUTH && allowSmsSignatureDelivery)
  || (!canUpgradeSmsSignatureDeliveryTier && allowSmsSignatureDelivery);

  // Display text input depending on which authentication method is selected
  const handleSelection = React.useCallback((
    selection: AuthenticationTypes,
  ) => {
    /**
     * MenuItems are actually <li> elements, which don't take disabled attribute
     * They can be made to look disabled with CSS, but we have to prevent selecting this element
     * if Email + SMS is the delivery type.
     */
    if (selection === 'smsAuthNumber' && smsAuthMenuItemDisabled) {
      return;
    }
    authTypeHelpers.setValue(selection);
  }, [authTypeHelpers, smsAuthMenuItemDisabled]);

  React.useEffect(() => {
    if (authTypeMeta.value === 'accessCode') {
      // If access code is not enabled, set authType as smsAuthNumber
      if (!isDefaultOrAccessCode) {
        authTypeHelpers.setValue('smsAuthNumber');
      } else if (smsAuthNumberMeta.value) {
        smsAuthNumberHelpers.setValue('');
      }
    } else if (authTypeMeta.value === 'smsAuthNumber') {
      if (accessCodeMeta.value) {
        accessCodeHelpers.setValue('');
      }
    }
  }, [
    accessCodeHelpers,
    accessCodeMeta.value,
    authTypeHelpers,
    authTypeMeta.value,
    isDefaultOrAccessCode,
    smsAuthNumberHelpers,
    smsAuthNumberMeta.value,
  ]);

  const handleMouseOver = (e: any) => {
    if (smsAuthMenuItemDisabled) {
      setDisplayTooltip(true);
      setTooltipPosition({
        top: `${e.pageY}px`,
        left: `${e.pageX}px`,
      });
    }
  };

  const handleMouseLeave = () => {
    if (displayTooltip) {
      setDisplayTooltip(false);
    }
  };

  const smsAuthTooltipMessage = React.useMemo(() => {
    if (isSmsDelivery) {
      return intl.formatMessage(messages.smsAuthWithSmsDeliveryTooltip);
    } else if (numOfSmsAuths === MAX_SMS_AUTH && allowSmsSignatureDelivery) {
      return intl.formatMessage(messages.maxSmsAuthTooltip, { maxSmsAuths: MAX_SMS_AUTH });
    } else if (!canUpgradeSmsSignatureDeliveryTier && allowSmsSignatureDelivery) {
      return intl.formatMessage(messages.unableToAutoUpgradeSmsDelivery);
    }
    return null;
  }, [
    intl,
    isSmsDelivery,
    numOfSmsAuths,
    allowSmsSignatureDelivery,
    canUpgradeSmsSignatureDeliveryTier,
  ]);

  return (
    <div className={styles.authenticationFields} role="authenticationFields">
      <div className={styles.fieldRow}>
        <div
          className={classNames({
            [styles.errorFieldContainer]: ((accessCodeMeta.touched && accessCodeMeta.error)
            || (smsAuthNumberMeta.touched && smsAuthNumberMeta.error)),
          })}
        >
          <label>
            {intl.formatMessage(messages.authenticationMethodLabel)}
          </label>
          <TooltipUI
            arrow={ArrowDirection.None}
            style={tooltipPosition}
            className={classNames(styles.smsAuthDisabledTooltip, {
              [styles.displaySmsAuthDisabledTooltip]: displayTooltip,
            })}
          >
            {smsAuthTooltipMessage}
          </TooltipUI>
          <Dropdown.Wrapper
            kind='small'
            onSelection={handleSelection}
            className={styles.authenticationDropdown}
          >
            <Dropdown.Button>
              {dropdownButtonText}
            </Dropdown.Button>
            <Dropdown.Menu>
              {allowRecipientAccessCode && (
                <Dropdown.MenuItem tag='li' key='accessCode' value='accessCode'>
                  {intl.formatMessage(messages.accessCodeLabel)}
                </Dropdown.MenuItem>
              )}
              {allowRecipientSmsAuth
              && (
                <Dropdown.MenuItem
                  tag='li'
                  key='SMS'
                  value='smsAuthNumber'
                  className={classNames({
                    [styles.menuItemDisabled]: smsAuthMenuItemDisabled,
                  })}
                  onMouseOver={handleMouseOver}
                  onMouseLeave={handleMouseLeave}
                >
                  {intl.formatMessage(messages.recipientAuthenticationDropdownSmsAuth)}
                </Dropdown.MenuItem>
              )}
            </Dropdown.Menu>
          </Dropdown.Wrapper>
        </div>
        <div className={styles.fieldRow}>
          {isDefaultOrAccessCode && (
            <div
              className={classNames(
                styles.accessCodeField,
                {
                  [styles.errorFieldContainer]: (accessCodeMeta.touched && accessCodeMeta.error),
                },
              )}
            >
              <label htmlFor={accessCodeFieldName}>
                {intl.formatMessage(messages.recipientCodeLabel)}
                <div className={styles.infoIcon}>
                  <HelpIcon />
                  <TooltipUI className={styles.tooltip} arrow={ArrowDirection.Bottom}>
                    {intl.formatMessage(messages.recipientAccessCodeTooltip)}
                  </TooltipUI>
                </div>
              </label>
              <Field name={accessCodeFieldName}>
                {({ field }: FieldProps) => <TextField
                  id={accessCodeFieldName}
                  autoFocus={props.index === 0}
                  data-qa-ref={`${parent}.fields.accessCode`}
                  className={styles.accessCodeTextbox}
                  {...field} />}
              </Field>
              <div
                className={styles.errorContainer}
                role='accessCode-error'
                data-qa-ref='accessCode-error'
              >
                <ErrorMessage
                  name={accessCodeFieldName}
                  className={styles.error} />
              </div>
            </div>
          )}
          {isSmsAuth && (
            <div
              className={classNames({
                [styles.errorFieldContainer]: smsAuthNumberMeta.touched && smsAuthNumberMeta.error,
              })}
            >
              <label htmlFor={smsAuthFieldName}>
                {intl.formatMessage(messages.recipientPhoneNumberLabel)}
              </label>
              <Field name={smsAuthFieldName}>
                {({ field, form }: FieldProps) => (
                  <ContactInput
                    id={smsAuthFieldName}
                    autoFocus={props.index === 0}
                    className={styles.dropdownField}
                    data-qa-ref={`${parent}.fields.smsAuthNumber`}
                    placeholder='+1 5555555555'
                    contactTypes={['F']}
                    onSelectContact={(contact: types.Contact) => {
                      form.setFieldValue(field.name, contact.value);
                    }}
                    isSmsInput={true}
                    {...field}
                    onChange={(value: string) => form.setFieldValue(field.name, value)} />
                )}
              </Field>
              <div
                className={styles.errorContainer}
                role='smsAuthNumber-error'
                data-qa-ref='smsAuthNumber-error'
              >
                <ErrorMessage
                  name={smsAuthFieldName}
                  className={styles.error} />
              </div>
            </div>
          )}
        </div>
        <span className={styles.removeIcon} data-qa-ref='removeAuthFields' onClick={props.onClose} >
          <RemoveIcon />
        </span>
      </div>
    </div>
  );
};

export default AuthenticationFields;
